import { useLazyQuery } from '@apollo/client';
import SearchIcon from '@material-ui/icons/SearchRounded';
import { GET_PRODUCT } from 'connectors/queries/product';
import { isPartEligibleForWarranty } from 'pages/NewCaseSearch/helpers';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { authenticationState, loadingState, lookupState } from 'state/atoms';
import { UNKNOWN_STATUS, RMA_PART_PREFIX_REGEX, PART_NUMBER_REGEX } from 'utils/constants';
import { ErrorMessage, Form, SearchPartNumberInput, SubmitButton } from './styledComponents';

const FindPartNumber = () => {
  const [hasError, setError] = useState(false);
  const [isButtonEnabled, setButtonEnabled] = useState(false);
  const setLoading = useSetRecoilState(loadingState);
  const [currentLookupState, setLookupState] = useRecoilState(lookupState);
  const { parsedAuthData, endCustomerAccountId } = useRecoilValue(authenticationState);
  const [findProduct, { loading, data, error, refetch }] = useLazyQuery(GET_PRODUCT, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [partNumber, setPartNumber] = useState('');

  const handleOnChange = useCallback(
    (event) => {
      const { value } = event.currentTarget;
      const isInputValid = RMA_PART_PREFIX_REGEX.test(value);
      setButtonEnabled(PART_NUMBER_REGEX.test(value));
      setError(value === '___-_____' || !value ? false : !isInputValid);
      setPartNumber(value);
    },
    [setButtonEnabled, setPartNumber, setError]
  );

  useEffect(() => {
    if (!loading && !error && data) {
      const { product } = data.getAccount.getProduct || {};

      if (product && isPartEligibleForWarranty(product)) {
        setLookupState({
          ...currentLookupState,
          searchedPartNumber: partNumber.slice(0),
          selectedResult: {
            customerOracleID: parsedAuthData.OracleId,
            customerName: parsedAuthData.Account,
            serialNumber: currentLookupState.lookupResults[0].serialNumber,
            status: UNKNOWN_STATUS,
            product,
          },
        });
      } else {
        setLookupState({
          ...currentLookupState,
          selectedResult:
            currentLookupState.lookupResults.length === 1
              ? {
                  ...currentLookupState.selectedResult,
                  product: null,
                }
              : null,
          searchedPartNumber: partNumber.slice(0),
        });
        // Focus on search field in case of part not found
        document.getElementById('part-number-field').focus();
      }
    }
  }, [data, loading, error]);

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  const onSubmit = useCallback(
    (event) => {
      event.preventDefault();
      const { searchedPartNumber, lookupResults } = currentLookupState;

      // Highlight the field when no partNumber is provided
      if (!partNumber) {
        setError(true);
        return;
      }

      // Don't allow users to re-submit the search if it's
      // the same term previously searched
      if (partNumber === searchedPartNumber) {
        return;
      }

      // When the user tries to search for the same part number that is
      // matching the an existing result
      const allParts = lookupResults
        .filter((result) => result.product)
        .map((result) => result.product.partNumber);

      if (allParts.includes(partNumber)) {
        setLookupState({
          ...currentLookupState,
          searchedPartNumber: partNumber.slice(0),
        });

        return;
      }

      // Make sure the error state is not kept in the field
      setError(false);

      const searchProduct = refetch || findProduct;
      const payload = {
        variables: {
          accountId: endCustomerAccountId,
          input: { partNumber },
          images: {
            primaryOnly: true,
            sizes: ['Small'],
          },
        },
      };

      // On refetch we have to send the payload content only,
      // while on the first call we have to send the content wrapped into the object
      searchProduct(refetch ? payload.variables : { variables: payload.variables });
    },
    [currentLookupState, partNumber, refetch, findProduct, setError]
  );

  return (
    <Fragment>
      <Form onSubmit={onSubmit}>
        <SearchPartNumberInput
          id="part-number-field"
          placeholder="Part Number"
          disabled={loading}
          onChange={handleOnChange}
          value={partNumber}
          className={hasError ? 'has-error' : ''}
          autoFocus
        />

        <SubmitButton type="submit" disabled={!isButtonEnabled || loading}>
          <SearchIcon />
        </SubmitButton>
      </Form>
      {hasError && (
        <ErrorMessage>
          The part number provided is not a valid RMA eligible part number. Please check the part
          number and try again. Only 100-xxxxx and 120-xxxxx part numbers are RMA eligible.
        </ErrorMessage>
      )}
    </Fragment>
  );
};

export default FindPartNumber;
